import React, { useState } from "react";
import {
  Container,
  Form,
  Row,
  Col,
  Button,
  Card,
} from "react-bootstrap";
import { PencilSquare, StarFill } from "react-bootstrap-icons";

interface Question {
  category: string;
  text: string;
}

const questions: Question[] = [
  // Leadership & Vision
  { category: "Leadership & Vision", text: "My boss has a clear vision for our team or department." },
  { category: "Leadership & Vision", text: "My boss communicates goals and priorities effectively." },
  { category: "Leadership & Vision", text: "My boss demonstrates confidence and decisiveness." },
  { category: "Leadership & Vision", text: "My boss leads by example and models integrity." },
  { category: "Leadership & Vision", text: "My boss inspires and motivates the team." },

  // Communication & Feedback
  { category: "Communication & Feedback", text: "My boss is approachable and easy to talk to." },
  { category: "Communication & Feedback", text: "My boss listens actively and respects my input." },
  { category: "Communication & Feedback", text: "My boss provides constructive feedback regularly." },
  { category: "Communication & Feedback", text: "My boss keeps the team informed about important issues." },
  { category: "Communication & Feedback", text: "My boss handles conflict or disagreements professionally." },

  // Support & Development
  { category: "Support & Development", text: "My boss supports my career growth and development." },
  { category: "Support & Development", text: "I am encouraged to learn new skills." },
  { category: "Support & Development", text: "My boss recognizes and appreciates my contributions." },
  { category: "Support & Development", text: "I feel safe to make mistakes and learn from them." },
  { category: "Support & Development", text: "My boss offers resources or guidance when needed." },

  // Fairness & Respect
  { category: "Fairness & Respect", text: "My boss treats everyone fairly and consistently." },
  { category: "Fairness & Respect", text: "My boss values diversity and inclusion." },
  { category: "Fairness & Respect", text: "My boss avoids favoritism or bias." },
  { category: "Fairness & Respect", text: "I feel respected and valued by my boss." },
  { category: "Fairness & Respect", text: "My workload and expectations are reasonable." },

  // Overall Job Satisfaction
  { category: "Overall Job Satisfaction", text: "I am generally satisfied with my job." },
  { category: "Overall Job Satisfaction", text: "I feel proud to be part of this team." },
  { category: "Overall Job Satisfaction", text: "I would recommend working under this boss to others." },
  { category: "Overall Job Satisfaction", text: "I feel my work has purpose and impact." },
  { category: "Overall Job Satisfaction", text: "I see a future for myself at this organization." },
];

const categories = Array.from(new Set(questions.map((q) => q.category)));

const BossScoreCard: React.FC = () => {
  const [ratings, setRatings] = useState<number[]>(Array(questions.length).fill(0));
  const [comments, setComments] = useState({ strength: "", improvement: "", suggestions: "" });

  const handleRatingChange = (index: number, value: number) => {
    const updatedRatings = [...ratings];
    updatedRatings[index] = value;
    setRatings(updatedRatings);
  };

  const totalScore = ratings.reduce((acc, curr) => acc + curr, 0);

  const getEvaluation = (score: number) => {
    if (score >= 101) return "Excellent – Strong leadership and a positive job situation";
    if (score >= 76) return "Good – Solid boss, with room for growth";
    if (score >= 51) return "Fair – Mixed experience; consider discussing concerns";
    return "Poor – Consider whether this job/boss is right for you";
  };

  return (
    <Container className="my-4">
      <h2 className="mb-4">
        <StarFill className="text-warning me-2" /> Boss Score Card
      </h2>
      {categories.map((category) => (
        <Card className="mb-4" key={category}>
          <Card.Header><strong>{category}</strong></Card.Header>
          <Card.Body>
            {questions.filter((q) => q.category === category).map((q, i) => {
              const index = questions.indexOf(q);
              return (
                <Form.Group as={Row} className="mb-3" key={index}>
                  <Form.Label column sm={8}>{q.text}</Form.Label>
                  <Col sm={4}>
                    <Form.Select
                      value={ratings[index]}
                      onChange={(e) => handleRatingChange(index, parseInt(e.target.value))}
                    >
                      <option value={0}>Select</option>
                      {[1, 2, 3, 4, 5].map((val) => (
                        <option key={val} value={val}>{val}</option>
                      ))}
                    </Form.Select>
                  </Col>
                </Form.Group>
              );
            })}
          </Card.Body>
        </Card>
      ))}

      <Card className="mb-4">
        <Card.Header>Optional Comments</Card.Header>
        <Card.Body>
          <Form.Group className="mb-3">
            <Form.Label>Greatest strength of your boss:</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={comments.strength}
              onChange={(e) => setComments({ ...comments, strength: e.target.value })}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Area for improvement:</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={comments.improvement}
              onChange={(e) => setComments({ ...comments, improvement: e.target.value })}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Suggestions for a better work environment:</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={comments.suggestions}
              onChange={(e) => setComments({ ...comments, suggestions: e.target.value })}
            />
          </Form.Group>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Total Score: {totalScore} / 125</h4>
          <p><strong>{getEvaluation(totalScore)}</strong></p>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default BossScoreCard;
